import React, { useState, useEffect } from 'react';
import { 
  Download, Filter, RotateCcw, BarChart3, TrendingUp, 
  DollarSign, Users, Target, Calendar, Search, UserPlus,
  Phone, Mail, MapPin, Clock, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';

const LeadReports = () => {
  // State management
  const [filters, setFilters] = useState({
    dateRange: '30',
    status: 'all',
    source: 'all',
    priority: 'all',
    assignedTo: 'all',
    search: ''
  });

  const [leadData, setLeadData] = useState([]);
  const [kpis, setKpis] = useState({
    totalLeads: 0,
    newLeads: 0,
    qualifiedLeads: 0,
    conversionRate: 0,
    avgResponseTime: 0,
    totalValue: 0
  });

  // Filter handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilter = () => {
    console.log('Applying filters:', filters);
    loadLeadData();
  };

  const handleResetFilter = () => {
    setFilters({
      dateRange: '30',
      status: 'all',
      source: 'all',
      priority: 'all',
      assignedTo: 'all',
      search: ''
    });
    loadLeadData();
  };

  // Export handlers
  const handleExportPDF = () => {
    console.log('Exporting Lead Reports to PDF');
  };

  const handleExportExcel = () => {
    console.log('Exporting Lead Reports to Excel');
  };

  // Load data
  useEffect(() => {
    loadLeadData();
  }, []);

  const loadLeadData = () => {
    // Dummy data for Lead Reports
    const dummyLeads = [
      {
        id: 'LD-001',
        leadName: 'TechCorp Solutions',
        contactPerson: 'John Smith',
        email: 'john@techcorp.com',
        phone: '+1-555-0123',
        company: 'TechCorp Solutions',
        industry: 'Technology',
        status: 'New',
        source: 'Website',
        priority: 'High',
        assignedTo: 'Sarah Johnson',
        value: 50000,
        createdDate: '2024-01-15',
        lastContact: '2024-01-20',
        responseTime: '2 hours',
        location: 'New York, NY'
      },
      {
        id: 'LD-002',
        leadName: 'Global Manufacturing Inc',
        contactPerson: 'Maria Garcia',
        email: 'maria@globalmfg.com',
        phone: '+1-555-0456',
        company: 'Global Manufacturing Inc',
        industry: 'Manufacturing',
        status: 'Qualified',
        source: 'Referral',
        priority: 'Medium',
        assignedTo: 'Mike Chen',
        value: 75000,
        createdDate: '2024-01-12',
        lastContact: '2024-01-19',
        responseTime: '4 hours',
        location: 'Chicago, IL'
      },
      {
        id: 'LD-003',
        leadName: 'RetailMax Stores',
        contactPerson: 'David Wilson',
        email: 'david@retailmax.com',
        phone: '+1-555-0789',
        company: 'RetailMax Stores',
        industry: 'Retail',
        status: 'Contacted',
        source: 'Cold Call',
        priority: 'Low',
        assignedTo: 'Lisa Brown',
        value: 30000,
        createdDate: '2024-01-10',
        lastContact: '2024-01-18',
        responseTime: '1 day',
        location: 'Los Angeles, CA'
      },
      {
        id: 'LD-004',
        leadName: 'HealthCare Plus',
        contactPerson: 'Dr. Emily Davis',
        email: 'emily@healthcareplus.com',
        phone: '+1-555-0321',
        company: 'HealthCare Plus',
        industry: 'Healthcare',
        status: 'New',
        source: 'Trade Show',
        priority: 'High',
        assignedTo: 'Tom Anderson',
        value: 120000,
        createdDate: '2024-01-22',
        lastContact: '2024-01-22',
        responseTime: '30 minutes',
        location: 'Boston, MA'
      },
      {
        id: 'LD-005',
        leadName: 'FinanceFirst Bank',
        contactPerson: 'Robert Taylor',
        email: 'robert@financefirst.com',
        phone: '+1-555-0654',
        company: 'FinanceFirst Bank',
        industry: 'Finance',
        status: 'Qualified',
        source: 'Website',
        priority: 'High',
        assignedTo: 'Sarah Johnson',
        value: 200000,
        createdDate: '2024-01-08',
        lastContact: '2024-01-17',
        responseTime: '3 hours',
        location: 'Miami, FL'
      }
    ];

    setLeadData(dummyLeads);

    // Calculate KPIs
    const totalLeads = dummyLeads.length;
    const newLeads = dummyLeads.filter(lead => lead.status === 'New').length;
    const qualifiedLeads = dummyLeads.filter(lead => lead.status === 'Qualified').length;
    const conversionRate = totalLeads > 0 ? ((qualifiedLeads / totalLeads) * 100).toFixed(1) : 0;
    const avgResponseTime = '2.5 hours';
    const totalValue = dummyLeads.reduce((sum, lead) => sum + lead.value, 0);

    setKpis({
      totalLeads,
      newLeads,
      qualifiedLeads,
      conversionRate,
      avgResponseTime,
      totalValue
    });
  };

  // Helper functions
  const getStatusBadge = (status) => {
    const statusConfig = {
      'New': { color: '#3b82f6', bg: '#dbeafe', icon: UserPlus },
      'Contacted': { color: '#f59e0b', bg: '#fef3c7', icon: Phone },
      'Qualified': { color: '#10b981', bg: '#d1fae5', icon: CheckCircle },
      'Unqualified': { color: '#ef4444', bg: '#fee2e2', icon: XCircle },
      'On Hold': { color: 'var(--color-text-secondary, #5B6478)', bg: '#f3f4f6', icon: AlertCircle }
    };

    const config = statusConfig[status] || statusConfig['New'];
    const IconComponent = config.icon;

    return (
      <span 
        style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          gap: '4px',
          padding: '4px 8px', 
          borderRadius: '12px', 
          fontSize: '11px', 
          fontWeight: '500',
          backgroundColor: config.bg,
          color: config.color
        }}
      >
        <IconComponent size={10} />
        {status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      'High': { color: '#dc2626', bg: '#fee2e2' },
      'Medium': { color: '#d97706', bg: '#fef3c7' },
      'Low': { color: '#059669', bg: '#d1fae5' }
    };

    const config = priorityConfig[priority] || priorityConfig['Medium'];

    return (
      <span 
        style={{ 
          padding: '2px 6px', 
          borderRadius: '8px', 
          fontSize: '10px', 
          fontWeight: '500',
          backgroundColor: config.bg,
          color: config.color
        }}
      >
        {priority}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="sales-dashboard" style={{ fontSize: '14px' }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Lead Reports</h1>
          <p>Comprehensive lead analytics and pipeline insights</p>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-secondary" onClick={handleExportPDF}>
            <Download size={16} />
            Export PDF
          </button>
          <button className="btn-primary" onClick={handleExportExcel}>
            <Download size={16} />
            Export Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="form-section">
        <div className="section-header" style={{ marginLeft: '20px' }}>
          <h3>Report Filters</h3>
          <p>Customize your lead analysis and reporting</p>
        </div>
        <div className="form-content">
          <div className="grid-row-4">
            <div className="form-group">
              <label className="form-label">Date Range</label>
              <select 
                className="form-select" 
                name="dateRange" 
                value={filters.dateRange}
                onChange={handleInputChange}
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Lead Status</label>
              <select 
                className="form-select" 
                name="status" 
                value={filters.status}
                onChange={handleInputChange}
              >
                <option value="all">All Status</option>
                <option value="New">New</option>
                <option value="Contacted">Contacted</option>
                <option value="Qualified">Qualified</option>
                <option value="Unqualified">Unqualified</option>
                <option value="On Hold">On Hold</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Lead Source</label>
              <select 
                className="form-select" 
                name="source" 
                value={filters.source}
                onChange={handleInputChange}
              >
                <option value="all">All Sources</option>
                <option value="Website">Website</option>
                <option value="Referral">Referral</option>
                <option value="Cold Call">Cold Call</option>
                <option value="Trade Show">Trade Show</option>
                <option value="Social Media">Social Media</option>
                <option value="Email Campaign">Email Campaign</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select 
                className="form-select" 
                name="priority" 
                value={filters.priority}
                onChange={handleInputChange}
              >
                <option value="all">All Priorities</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>
          <div className="grid-row-2">
            <div className="form-group">
              <label className="form-label">Assigned To</label>
              <select 
                className="form-select" 
                name="assignedTo" 
                value={filters.assignedTo}
                onChange={handleInputChange}
              >
                <option value="all">All Team Members</option>
                <option value="Sarah Johnson">Sarah Johnson</option>
                <option value="Mike Chen">Mike Chen</option>
                <option value="Lisa Brown">Lisa Brown</option>
                <option value="Tom Anderson">Tom Anderson</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Search Leads</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="form-input"
                  name="search"
                  value={filters.search}
                  onChange={handleInputChange}
                  placeholder="Search by company, contact, or email..."
                  style={{ paddingLeft: '40px' }}
                />
                <Search 
                  size={16} 
                  style={{ 
                    position: 'absolute', 
                    left: '12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    color: 'var(--color-text-secondary, #5B6478)' 
                  }} 
                />
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button onClick={handleApplyFilter} className="btn-primary">
              <Filter size={16} /> Apply Filter
            </button>
            <button onClick={handleResetFilter} className="btn-secondary">
              <RotateCcw size={16} /> Reset Filter
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{kpis.totalLeads}</div>
          <div className="metric-title">Total Leads</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <UserPlus size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{kpis.newLeads}</div>
          <div className="metric-title">New Leads</div>
          <div className="metric-subtitle">this month</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Target size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+5%</span>
            </div>
          </div>
          <div className="metric-value">{kpis.qualifiedLeads}</div>
          <div className="metric-title">Qualified Leads</div>
          <div className="metric-subtitle">ready for sales</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <BarChart3 size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+3%</span>
            </div>
          </div>
          <div className="metric-value">{kpis.conversionRate}%</div>
          <div className="metric-title">Conversion Rate</div>
          <div className="metric-subtitle">new to qualified</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend negative">
              <TrendingUp size={12} />
              <span>-20%</span>
            </div>
          </div>
          <div className="metric-value">{kpis.avgResponseTime}</div>
          <div className="metric-title">Avg Response Time</div>
          <div className="metric-subtitle">faster response</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+18%</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(kpis.totalValue)}</div>
          <div className="metric-title">Total Lead Value</div>
          <div className="metric-subtitle">pipeline potential</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="form-section">
        <div className="section-header" style={{ marginLeft: '20px' }}>
          <h3>Lead Pipeline Details</h3>
          <p>Detailed view of all leads and their current status</p>
        </div>
        <div className="form-content">
          <div className="data-table-container" style={{ fontSize: '13px' }}>
            <table className="data-table" style={{ fontSize: '13px', width: '100%', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Lead Information</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Status</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Source</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Priority</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Assigned To</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Value</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Created</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Last Contact</th>
                  <th style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>Response Time</th>
                </tr>
              </thead>
              <tbody>
                {leadData.map((lead) => (
                  <tr key={lead.id} style={{ fontSize: '13px' }}>
                    <td style={{ padding: '12px 8px', minWidth: '200px' }}>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '13px', lineHeight: '1.3' }}>
                          {lead.leadName}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '2px' }}>
                          {lead.contactPerson} • {lead.id}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Mail size={10} />
                          {lead.email}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Phone size={10} />
                          {lead.phone}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <MapPin size={10} />
                          {lead.location}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      {getStatusBadge(lead.status)}
                    </td>
                    <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                      {lead.source}
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      {getPriorityBadge(lead.priority)}
                    </td>
                    <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                      {lead.assignedTo}
                    </td>
                    <td style={{ padding: '12px 8px', whiteSpace: 'nowrap', fontWeight: '500' }}>
                      {formatCurrency(lead.value)}
                    </td>
                    <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                      {lead.createdDate}
                    </td>
                    <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                      {lead.lastContact}
                    </td>
                    <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                      {lead.responseTime}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Summary Footer */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
          </div>
          <div className="metric-value">{formatCurrency(kpis.totalValue)}</div>
          <div className="metric-title">Total Lead Value</div>
          <div className="metric-subtitle">Pipeline potential</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
          </div>
          <div className="metric-value">{kpis.totalLeads}</div>
          <div className="metric-title">Total Leads</div>
          <div className="metric-subtitle">In pipeline</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Target size={16} />
            </div>
          </div>
          <div className="metric-value">{kpis.conversionRate}%</div>
          <div className="metric-title">Conversion Rate</div>
          <div className="metric-subtitle">Lead quality</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
          </div>
          <div className="metric-value">{kpis.avgResponseTime}</div>
          <div className="metric-title">Avg Response Time</div>
          <div className="metric-subtitle">Team efficiency</div>
        </div>
      </div>
    </div>
  );
};

export default LeadReports;
